import React from 'react';
import { useForm } from 'react-hook-form';
import { SingleValue } from 'react-select';
import { ActionIcon } from '@mantine/core';

import APIError from 'types/api_error';
import { UserGroup } from 'types/user_group';

import { useCurrentApplication } from 'application/hooks/useCurrentApplication';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { rolesHaveApplicationAccess } from 'hooks/useHasAccess';
import { useMembers } from 'settings/hooks/useMembers';
import { useUserGroupMembers } from 'settings/hooks/useUserGroupMembers';
import { useAddUserGroupMember } from 'settings/hooks/useAddUserGroupMember';
import { useRemoveUserGroupMembers } from 'settings/hooks/useRemoveUserGroupMembers';
import { useCreateUserGroup } from 'settings/hooks/useCreateUserGroup';
import { useUpdateUserGroup } from 'settings/hooks/useUpdateUserGroup';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Drawer } from 'components/Drawer/DrawerDeprecated';
import { Field } from 'components/Field/Field';
import { FormErrorMessage } from 'components/FormMessage/FormErrorMessage';
import { Icon } from 'components/Icon/IconDeprecated';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { Select } from 'components/Select/Select';
import { Text } from 'components/Text/Text';

type MemberOption = { value: string; label: string };

type MemberDisplay = { id: string; name: string; email: string };

type UserGroupFieldValues = {
  name: string;
};

const FORM_ID = 'user-group-form';

type UserGroupFormProps = {
  open: boolean;
  onClose: () => void;
  group?: UserGroup;
};

export function UserGroupForm({ open, onClose, group }: UserGroupFormProps) {
  const isEditing = Boolean(group);
  const isDefault = Boolean(group?.is_default);

  // Track local member changes (only applied on save)
  const [pendingAdds, setPendingAdds] = React.useState<Set<string>>(new Set());
  const [pendingRemoves, setPendingRemoves] = React.useState<Set<string>>(
    new Set()
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [memberError, setMemberError] = React.useState<APIError>();

  // When creating, the group is created first and members are added in a
  // follow-up call. If the member step fails we keep a reference to the
  // already-created group so a retry edits it instead of creating a duplicate,
  // and track which members were persisted so we don't re-add them.
  const createdGroupRef = React.useRef<UserGroup | null>(null);
  const persistedAddsRef = React.useRef<Set<string>>(new Set());

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<UserGroupFieldValues>();

  React.useEffect(() => {
    if (open) {
      reset({ name: group?.name ?? '' });
      setPendingAdds(new Set());
      setPendingRemoves(new Set());
      setMemberError(undefined);
      createdGroupRef.current = null;
      persistedAddsRef.current = new Set();
    }
  }, [open, group, reset]);

  const { mutateAsync: createGroup, error: createError } = useCreateUserGroup();

  const { mutateAsync: updateGroup, error: updateError } = useUpdateUserGroup();

  const { mutateAsync: addMember } = useAddUserGroupMember();
  const { mutateAsync: removeMembers } = useRemoveUserGroupMembers();

  const isSaving = isSubmitting;
  const error = createError || updateError || memberError;

  const { data: application } = useCurrentApplication();
  const { data: organization } = useCurrentOrganization();

  // Members data (only relevant when editing)
  const { data: membersData } = useMembers({ params: { limit: 200 } });
  const { data: groupMembers, isLoading: isLoadingMembers } =
    useUserGroupMembers({
      groupId: group?.id ?? '',
      enabled: Boolean(group),
    });

  const allMembers = membersData?.data ?? [];
  const serverMemberIds = React.useMemo(
    () => new Set(groupMembers?.map((m) => m.account_id) ?? []),
    [groupMembers]
  );

  // Effective member IDs = server state + pending adds - pending removes
  const effectiveMemberIds = React.useMemo(() => {
    const ids = new Set(serverMemberIds);
    for (const id of pendingAdds) ids.add(id);
    for (const id of pendingRemoves) ids.delete(id);
    return ids;
  }, [serverMemberIds, pendingAdds, pendingRemoves]);

  const displayMembers: MemberDisplay[] = allMembers.filter((m) =>
    effectiveMemberIds.has(m.id)
  );

  // Only members with access to this workspace — an organization-level role
  // (owner/billing/manager) or a role scoped to this workspace — can be added.
  const availableOptions: MemberOption[] = allMembers
    .filter(
      (m) =>
        !effectiveMemberIds.has(m.id) &&
        application &&
        organization &&
        rolesHaveApplicationAccess(m.roles, application.id, organization.id)
    )
    .map((m) => ({ value: m.id, label: m.name || m.email }));

  function handleAddMemberLocal(option: SingleValue<MemberOption>) {
    if (!option) return;
    const id = option.value;
    setPendingAdds((prev) => new Set(prev).add(id));
    setPendingRemoves((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function handleRemoveMemberLocal(accountId: string) {
    if (serverMemberIds.has(accountId)) {
      setPendingRemoves((prev) => new Set(prev).add(accountId));
    }
    setPendingAdds((prev) => {
      const next = new Set(prev);
      next.delete(accountId);
      return next;
    });
  }

  async function persistMemberChanges(groupId: string) {
    if (pendingRemoves.size > 0) {
      await removeMembers({ groupId, account_ids: [...pendingRemoves] });
      // Removals are done; don't re-attempt them if a later add fails.
      setPendingRemoves(new Set());
    }
    // Add members one at a time so a single failure leaves the rest intact and
    // a retry only re-attempts the members that haven't been persisted yet.
    for (const accountId of pendingAdds) {
      if (persistedAddsRef.current.has(accountId)) continue;
      await addMember({ groupId, account_id: accountId });
      persistedAddsRef.current.add(accountId);
    }
  }

  async function onSubmit({ name }: UserGroupFieldValues) {
    setMemberError(undefined);
    setIsSubmitting(true);

    try {
      // Step 1: create or update the group itself.
      let groupId: string;
      try {
        if (group) {
          await updateGroup({ id: group.id, name });
          groupId = group.id;
        } else if (createdGroupRef.current) {
          // Group was created in a previous attempt that failed while adding
          // members — reuse it instead of creating a duplicate.
          groupId = createdGroupRef.current.id;
        } else {
          const created = await createGroup({ name });
          createdGroupRef.current = created;
          groupId = created.id;
        }
      } catch {
        // Surfaced via createError / updateError.
        return;
      }

      // Step 2: apply the selected member changes against the saved group.
      try {
        await persistMemberChanges(groupId);
      } catch (memberErr) {
        setMemberError(memberErr as APIError);
        return;
      }

      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={getDrawerTitle({ isDefault, isEditing })}
      footer={
        <ButtonGroup align="end">
          <Button variant="secondary" onClick={onClose}>
            {isDefault ? 'Close' : 'Cancel'}
          </Button>
          {!isDefault && (
            <Button
              variant="primary"
              type="submit"
              form={FORM_ID}
              loading={isSaving}
            >
              {isEditing ? 'Save' : 'Create'}
            </Button>
          )}
        </ButtonGroup>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
      >
        <FormErrorMessage error={error} />

        <Field id="group-name" label="Group name" error={errors.name} required>
          <Input
            {...register('name', {
              required: 'Please enter a group name.',
            })}
            autoFocus={!isDefault}
            disabled={isDefault}
          />
        </Field>

        <hr className="my-1 border-t border-gray-200" />

        <Field label="Members">
          {isDefault ? (
            <div className="flex flex-col gap-3">
              <Text>
                This is the default group. All workspace members are
                automatically included.
              </Text>

              {isEditing && isLoadingMembers && (
                <Loader size="small" text="Loading members..." />
              )}
              {!(isEditing && isLoadingMembers) &&
                displayMembers.length === 0 && (
                  <Text>No members in this group yet.</Text>
                )}
              {!(isEditing && isLoadingMembers) &&
                displayMembers.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {displayMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center border-b border-b-[hsl(var(--ui-separator))] py-1.5"
                      >
                        <div>
                          <Text>
                            <strong>{member.name || member.email}</strong>
                          </Text>
                          {member.name && (
                            <Text size="small">{member.email}</Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Select<MemberOption>
                value={null}
                onChange={handleAddMemberLocal}
                placeholder="Select a member to add..."
                options={availableOptions}
                isClearable
              />

              {isEditing && isLoadingMembers && (
                <Loader size="small" text="Loading members..." />
              )}
              {!(isEditing && isLoadingMembers) &&
                displayMembers.length === 0 && (
                  <Text>No members in this group yet.</Text>
                )}
              {!(isEditing && isLoadingMembers) &&
                displayMembers.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {displayMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between border-b border-b-[hsl(var(--ui-separator))] py-1.5"
                      >
                        <div>
                          <Text>
                            <strong>{member.name || member.email}</strong>
                          </Text>
                          {member.name && (
                            <Text size="small">{member.email}</Text>
                          )}
                        </div>
                        <ActionIcon
                          aria-label={`Remove ${member.name || member.email}`}
                          color="red"
                          variant="subtle"
                          onClick={() => handleRemoveMemberLocal(member.id)}
                        >
                          <Icon name="delete" unstyled />
                        </ActionIcon>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}
        </Field>
      </form>
    </Drawer>
  );
}

function getDrawerTitle({
  isDefault,
  isEditing,
}: {
  isDefault: boolean;
  isEditing: boolean;
}): string {
  if (isDefault) return 'Default user group';
  if (isEditing) return 'Edit user group';
  return 'Create user group';
}
